export type RecordCameraPose = {
  pos: [number, number, number]
  yaw: number
  pitch: number
}

/** Position delta (meters) that counts as camera motion on a large stage. */
export const RECORD_MOTION_EPS_POS = 0.02
/** Four frames at 60 Hz. Keeps a real fly step above the position threshold. */
export const RECORD_MOTION_FRAME_DIVISOR = 240

/** Position threshold for the live fly speed. Never above {@link RECORD_MOTION_EPS_POS}. */
export function recordMotionEpsPos(metersPerSecond: number): number {
  if (!Number.isFinite(metersPerSecond) || metersPerSecond <= 0) return RECORD_MOTION_EPS_POS
  return Math.min(RECORD_MOTION_EPS_POS, metersPerSecond / RECORD_MOTION_FRAME_DIVISOR)
}
/** Yaw/pitch delta (radians) that counts as camera motion. */
export const RECORD_MOTION_EPS_RAD = 0.01
/** Wall-time without motion before freezing the motion clock. */
export const RECORD_IDLE_DEBOUNCE_MS = 200

export function shortestAngleDelta(a: number, b: number): number {
  let d = b - a
  while (d > Math.PI) d -= Math.PI * 2
  while (d < -Math.PI) d += Math.PI * 2
  return d
}

export function cameraPoseMoved(
  prev: RecordCameraPose,
  next: RecordCameraPose,
  epsPos = RECORD_MOTION_EPS_POS,
  epsRad = RECORD_MOTION_EPS_RAD,
): boolean {
  const dx = next.pos[0] - prev.pos[0]
  const dy = next.pos[1] - prev.pos[1]
  const dz = next.pos[2] - prev.pos[2]
  if (dx * dx + dy * dy + dz * dz > epsPos * epsPos) return true
  if (Math.abs(shortestAngleDelta(prev.yaw, next.yaw)) > epsRad) return true
  if (Math.abs(shortestAngleDelta(prev.pitch, next.pitch)) > epsRad) return true
  return false
}

function clonePose(pose: RecordCameraPose): RecordCameraPose {
  return { pos: [...pose.pos] as [number, number, number], yaw: pose.yaw, pitch: pose.pitch }
}

export type RecordMotionClock = {
  motionMs: number
  lastWallMs: number
  /** Pose from the previous tick — drives idle / freeze. */
  lastTickPose: RecordCameraPose
  /** Last successfully recorded sample; null until first append. */
  lastSamplePose: RecordCameraPose | null
  idleSinceWallMs: number | null
  paused: boolean
}

export type RecordMotionTick = {
  clock: RecordMotionClock
  motionMs: number
  shouldAppend: boolean
  paused: boolean
}

export function createRecordMotionClock(nowWallMs: number, pose: RecordCameraPose): RecordMotionClock {
  return {
    motionMs: 0,
    lastWallMs: nowWallMs,
    lastTickPose: clonePose(pose),
    // null → first tick seeds a sample at t=0 even if pose equals begin pose
    lastSamplePose: null,
    idleSinceWallMs: null,
    paused: false,
  }
}

/**
 * Advance motion-gated record clock.
 * - frameMoved (vs lastTickPose) → clock advance / idle / freeze
 * - sampleMoved (vs lastSamplePose) → shouldAppend when also frame-moving
 * Provisional idle (!frameMoved, not yet paused) does NOT advance motionMs;
 * resume before debounce credits the idle gap so micro-pauses do not cut the path.
 * Resume after pause discards the gap (no pause-tax in the path).
 * Append throttle stays in appendCameraSample; caller updates lastSamplePose via markSampleAppended.
 */
export function tickRecordMotionClock(
  clock: RecordMotionClock,
  nowWallMs: number,
  pose: RecordCameraPose,
  epsPos = RECORD_MOTION_EPS_POS,
): RecordMotionTick {
  const dt = Math.max(0, nowWallMs - clock.lastWallMs)
  const seed = clock.lastSamplePose == null
  const frameMoved = cameraPoseMoved(clock.lastTickPose, pose, epsPos)
  const sampleMoved = clock.lastSamplePose == null || cameraPoseMoved(clock.lastSamplePose, pose, epsPos)
  const shouldAppend = seed || (frameMoved && sampleMoved)
  const nextTickPose = clonePose(pose)

  if (frameMoved || seed) {
    if (clock.paused && frameMoved) {
      // Resume from freeze: drop idle wall gap.
      return {
        clock: {
          ...clock,
          lastWallMs: nowWallMs,
          lastTickPose: nextTickPose,
          idleSinceWallMs: null,
          paused: false,
        },
        motionMs: clock.motionMs,
        shouldAppend,
        paused: false,
      }
    }

    // Resume from provisional idle: credit gap so micro-pauses stay continuous.
    const idleGap = !clock.paused && clock.idleSinceWallMs != null ? Math.max(0, nowWallMs - clock.idleSinceWallMs) : 0
    const motionMs = clock.motionMs + (idleGap > 0 ? idleGap : dt)
    return {
      clock: {
        ...clock,
        motionMs,
        lastWallMs: nowWallMs,
        lastTickPose: nextTickPose,
        idleSinceWallMs: null,
        paused: false,
      },
      motionMs,
      shouldAppend,
      paused: false,
    }
  }

  // Idle: no frame-to-frame motion (even if still far from last sample).
  // Anchor idle to last motion wall so a later grace-resume can credit the full gap.
  const idleSince = clock.idleSinceWallMs ?? clock.lastWallMs
  if (clock.paused) {
    return {
      clock: {
        ...clock,
        lastWallMs: nowWallMs,
        lastTickPose: nextTickPose,
        idleSinceWallMs: idleSince,
      },
      motionMs: clock.motionMs,
      shouldAppend: false,
      paused: true,
    }
  }

  const idleFor = nowWallMs - idleSince
  if (idleFor >= RECORD_IDLE_DEBOUNCE_MS) {
    // Enter freeze — discard provisional gap (do not bake pause into path).
    return {
      clock: {
        ...clock,
        lastWallMs: nowWallMs,
        lastTickPose: nextTickPose,
        idleSinceWallMs: idleSince,
        paused: true,
      },
      motionMs: clock.motionMs,
      shouldAppend: false,
      paused: true,
    }
  }

  // Provisional grace: hold motionMs, no append.
  return {
    clock: {
      ...clock,
      lastWallMs: nowWallMs,
      lastTickPose: nextTickPose,
      idleSinceWallMs: idleSince,
      paused: false,
    },
    motionMs: clock.motionMs,
    shouldAppend: false,
    paused: false,
  }
}

export function markSampleAppended(clock: RecordMotionClock, pose: RecordCameraPose): RecordMotionClock {
  return { ...clock, lastSamplePose: clonePose(pose) }
}
